import * as React from 'react';
import * as classNames from 'classnames';
import uniqueId = require('lodash/uniqueId');
import findIndex = require('lodash/findIndex');
import isEqual = require('lodash/isEqual');
import {ITests} from '../../redux/modules/console/reducer';
import {Scrollbars} from 'react-custom-scrollbars';
import {TestItem} from './testItem';
import {ConsoleScrollHint} from './consoleScrollHint';

export interface IEvaluationTestsProps {
  tests: ITests;
  consoleHintShown: boolean;
  ackConsoleScroll: () => void;
}

interface IEvaluationTestsState {
  consoleHintShown?: boolean;
  activeIndex?: number;
  isActive?: boolean;
}

export class ConsoleTestsResult extends React.Component<IEvaluationTestsProps, IEvaluationTestsState> {
  public state: IEvaluationTestsState = {
    consoleHintShown: false,
    activeIndex: 0,
    isActive: true,
  };

  private refTestsContainer: any;
  private refTestsContent: HTMLElement;

  public componentDidMount(): void {
    const activeIndex = this.getActiveTestIndex();
    const consoleHintShown = this.consoleHintShown();

    this.setState({
      consoleHintShown,
      activeIndex,
    }, () => {
      consoleHintShown && this.props.ackConsoleScroll();
    });
  }

  public shouldComponentUpdate(nextProps: IEvaluationTestsProps, newState: IEvaluationTestsState): boolean {
    return !isEqual(nextProps.tests, this.props.tests)
      || newState.consoleHintShown !== this.state.consoleHintShown
      || this.activeTestChanged(newState);
  }

  public render(): React.ReactElement<any> {
    const {tests: {passed, failed, wallTime, testResults, testErrors, meta} } = this.props;
    const {consoleHintShown} = this.state;
    const testsStatus = failed === 0 ? 'green' : 'red';

    return (
      <div className={`tests-wrapper tests-wrapper_${testsStatus}`}>
        <div className='tests-summary-info'>
          <span>Time: {wallTime}ms</span>

          <span className={classNames({'green': passed > 0})}>
            Passed: {passed}
          </span>

          <span className={classNames({'red': failed > 0})}>
            Failed: {failed}
          </span>
        </div>

        <Scrollbars ref={this.setRefTestsContainer}>
          <div className={'tests-content'} ref={this.setRefTestsContent}>
            {meta.map((metaItem: any, index: number) => (
              <div key={uniqueId('test-')}>
                <TestItem
                  title={`Test Case #${index + 1}`}
                  meta={metaItem}
                  test={testResults[index]}
                  testError={testErrors ? testErrors[index] : null}
                  opened={this.isOpened(index)}
                  onClick={() => this.setActiveTestIndex(index)}
                />
              </div>
            ))}
          </div>

          <ConsoleScrollHint visible={consoleHintShown} />
        </Scrollbars>
      </div>
    );
  }

  private setRefTestsContainer = (el: HTMLElement): void => {
    this.refTestsContainer = el;
  };

  private setRefTestsContent = (el: HTMLElement): void => {
    this.refTestsContent = el;
  };

  private isOpened = (index: number): boolean => {
    const {
      activeIndex,
      isActive,
    } = this.state;

    return activeIndex === index && isActive;
  };

  private getActiveTestIndex(): number {
    const {
      testResults,
      failed,
      meta,
    } = this.props.tests;

    if (meta.length === failed && testResults.length) {
      return 0;
    } else {
      const index = findIndex(testResults, {passed: false});
      return index >= 0 ? index : 0;
    }
  }

  private setActiveTestIndex = (index: number): void => {
    const {activeIndex, isActive} = this.state;

    this.setState({
      activeIndex: index,
      isActive: activeIndex === index
        ? !isActive
        : true,
    });
  };

  private activeTestChanged({activeIndex, isActive}: IEvaluationTestsState): boolean {
    return activeIndex !== this.state.activeIndex
      || isActive !== this.state.isActive;
  }

  private consoleHintShown = (): boolean => {
    const testsContainerHeight = this.refTestsContainer.getClientHeight();
    const testsContentHeight   = this.refTestsContent.clientHeight;

    return this.props.consoleHintShown
      && testsContentHeight > testsContainerHeight;
  }
}
